import Loading from 'common/components/Elements/Loading/Loading';
import { arrayLast } from 'common/utilities/core/array';
import { DEBOUNCE_TIME } from 'common/utilities/timeout/timeout';
import { enqueueInfoNotification } from 'core/redux/actions/notification/notificationActions';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import { useAppDispatch } from 'core/redux/store/configureStore';
import debounce from 'debounce';
import I18n from 'i18n/en-US';
import React from 'react';
import { useSelector } from 'react-redux';
import { arrangeBySimilarity } from 'sequenceAnalyser/api/api';
import type { SequenceAlign } from 'sequenceAnalyser/api/sequenceAnalyserApiClient';
import type { HandleAlignedSequencesChange } from 'sequenceAnalyser/components/SequenceAnalyser/SequenceAnalyser';

function useSortBySimilarity() {
  const dispatch = useAppDispatch();

  const alignmentOptions = useSelector((state: ApplicationState) => arrayLast(state.sequenceAnalyser.ranAlignments));

  const [loading, setLoading] = React.useState(false);
  const handleDelayedLoading = debounce((value: boolean) => setLoading(value), DEBOUNCE_TIME.fullscreenLoader);

  const sortBySimilarity = async (
    alignedSequences: SequenceAlign[],
    onAlignedSequencesChange: HandleAlignedSequencesChange,
  ) => {
    handleDelayedLoading(true);

    try {
      const sortedAlignedSequences = await arrangeBySimilarity(alignedSequences, alignmentOptions);
      onAlignedSequencesChange(sortedAlignedSequences);
      dispatch(enqueueInfoNotification(I18n.SequenceAnalyser.utilities.similarityTree.useSortBySimilarity.success));
    } finally {
      handleDelayedLoading(false);
    }
  };

  const SortBySimilarityLoading = loading ? <Loading inBackdrop /> : null;

  return { sortBySimilarity, SortBySimilarityLoading };
}

export default useSortBySimilarity;
